import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, IMAGES } from "../../config/landingConfig";
import { scrollToId } from "../../lib/scroll";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [showStickyCta, setShowStickyCta] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowStickyCta(window.scrollY > 620);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToId(href);
  };

  return (
    <>
      <header
        data-testid="main-nav"
        className="sticky top-0 z-50 border-b-2 border-ink bg-white/90 backdrop-blur-md"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <a
            href="#top"
            data-testid="nav-logo"
            onClick={(e) => go(e, "#top")}
            className="flex items-center gap-2"
          >
            {IMAGES.logoMark ? (
              <img src={IMAGES.logoMark} alt="Logo Anny Popławskiej" className="h-9 w-auto" />
            ) : (
              <span className="flex h-9 w-9 items-center justify-center rounded-xl border-2 border-ink bg-magenta shadow-hard-sm">
                <span className="text-base font-extrabold lowercase leading-none tracking-tight text-white">
                  ap
                </span>
              </span>
            )}
            <span className="font-display text-lg uppercase tracking-wide text-ink">
              AI&nbsp;Speaking&nbsp;Games
            </span>
          </a>

          <nav className="hidden items-center gap-6 lg:flex" aria-label="Menu główne">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`nav-link-${link.href.slice(1)}`}
                onClick={(e) => go(e, link.href)}
                className="text-sm font-bold text-ink/80 transition-colors hover:text-magenta"
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href="#pakiety"
              data-testid="nav-cta-button"
              onClick={(e) => {
                window.fbq?.("trackCustom", "ClickCTA", { location: "menu" });
                go(e, "#pakiety");
              }}
              className="hover-lift hidden rounded-full border-2 border-ink bg-magenta px-5 py-2.5 text-sm font-extrabold text-white shadow-hard-sm sm:inline-block"
            >
              Wybieram pakiet
            </a>
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? "Zamknij menu" : "Otwórz menu"}
              aria-expanded={open}
              className="flex h-10 w-10 items-center justify-center rounded-xl border-2 border-ink bg-white shadow-hard-sm lg:hidden"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.nav
              data-testid="nav-mobile-menu"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden border-t-2 border-ink bg-white lg:hidden"
              aria-label="Menu mobilne"
            >
              <div className="flex flex-col px-4 py-3">
                {NAV_LINKS.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    data-testid={`nav-mobile-link-${link.href.slice(1)}`}
                    onClick={(e) => go(e, link.href)}
                    className="border-b border-ink/10 py-3 text-base font-bold text-ink last:border-0"
                  >
                    {link.label}
                  </a>
                ))}
                <a
                  href="#pakiety"
                  data-testid="nav-mobile-cta-button"
                  onClick={(e) => go(e, "#pakiety")}
                  className="mt-3 mb-2 rounded-full border-2 border-ink bg-magenta px-5 py-3 text-center text-sm font-extrabold text-white shadow-hard-sm"
                >
                  Wybieram pakiet
                </a>
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </header>

      <AnimatePresence>
        {showStickyCta && (
          <motion.div
            data-testid="mobile-sticky-cta"
            initial={{ y: 80 }}
            animate={{ y: 0 }}
            exit={{ y: 80 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-x-0 bottom-0 z-40 border-t-2 border-ink bg-white/95 p-3 backdrop-blur md:hidden"
          >
            <a
              href="#pakiety"
              data-testid="mobile-sticky-cta-button"
              onClick={(e) => go(e, "#pakiety")}
              className="block rounded-full border-2 border-ink bg-magenta py-3 text-center text-sm font-extrabold text-white shadow-hard-sm"
            >
              Wybieram pakiet
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
